const productPaths = require('./product-paths.cjs')
const structuredData = require('./structured-data.cjs')
const entityStructuredData = require('./structured-data-entities.cjs')

const PRODUCT_TEMPLATE = 'component---src-templates-funds-product-template-jsx'
const PRODUCT_LISTING_TEMPLATE =
  'component---src-templates-funds-all-fund-template-jsx'
const LANDING_TEMPLATE = 'component---src-templates-landing-index-jsx'
const INSIGHT_ARTICLE_TEMPLATE =
  'component---src-templates-insights-insight-article-template-jsx'
const NEWS_ARTICLE_TEMPLATE =
  'component---src-templates-news-news-article-template-jsx'
const AUTHOR_PROFILE_TEMPLATE = 'component---src-templates-authors-index-jsx'
const INVESTMENT_TEAMS_TEMPLATE =
  'component---src-templates-investment-teams-template-jsx'
const INVESTMENT_TEAM_TEMPLATE =
  'component---src-templates-subadviser-firm-template-jsx'
const INVESTMENT_TEAM_MEMBER_TEMPLATE =
  'component---src-templates-subadviser-member-index-jsx'
const LEADERSHIP_PROFILE_TEMPLATE =
  'component---src-templates-leadership-team-member-leadership-team-member-template-jsx'
const LEADERSHIP_COLLECTION_TEMPLATE =
  'component---src-templates-about-us-leadership-template-jsx'
const PRODUCT_SUITE_HERO = 'Contentstack_hero_product_suite'
const ORGANIZATION_LANDING_PATHS = new Set(['/', '/about-us/discover-harbor/'])
const SHARE_CLASS_TO_PRODUCT_TYPE = {
  FUND: 'MUTUAL_FUND',
  ETF: 'ETF',
  CIT: 'CIT',
}

const getPageKind = (pageData) => {
  const component = pageData?.componentChunkName
  if (component === PRODUCT_TEMPLATE) return 'product'
  if (component === PRODUCT_LISTING_TEMPLATE) return 'collection'
  if (component === INSIGHT_ARTICLE_TEMPLATE) return 'article'
  if (component === NEWS_ARTICLE_TEMPLATE) return 'news-article'
  if (component === AUTHOR_PROFILE_TEMPLATE) return 'author-profile'
  if (component === INVESTMENT_TEAMS_TEMPLATE) return 'investment-teams'
  if (component === INVESTMENT_TEAM_TEMPLATE) return 'investment-team'
  if (component === INVESTMENT_TEAM_MEMBER_TEMPLATE) {
    return 'investment-team-member'
  }
  if (component === LEADERSHIP_PROFILE_TEMPLATE) return 'leadership-profile'
  if (component === LEADERSHIP_COLLECTION_TEMPLATE) {
    return 'leadership-collection'
  }
  if (component !== LANDING_TEMPLATE) return ''

  if (
    pageData?.result?.data?.contentstackLandingPage?.hero?.some(
      (hero) => hero?.__typename === PRODUCT_SUITE_HERO
    )
  ) {
    return 'suite'
  }

  return ORGANIZATION_LANDING_PATHS.has(pageData?.path)
    ? 'organization-page'
    : ''
}

const getTicker = (product) =>
  product?.fund_classes?.find((fundClass) => fundClass?.ticker)?.ticker || ''

const sortSuiteProducts = (products, reverseAlphaOrdering) => {
  const compareAlphabetically = (a, b) =>
    (getTicker(a) || a?.title || '').localeCompare(
      getTicker(b) || b?.title || ''
    )
  const sorted = [...products].sort(compareAlphabetically)
  const hasCompleteOrder =
    sorted.length > 0 &&
    sorted.every((product) => Number.isFinite(product?.suite_order))

  if (hasCompleteOrder) {
    return sorted.sort(
      (a, b) => a.suite_order - b.suite_order || compareAlphabetically(a, b)
    )
  }
  return reverseAlphaOrdering ? sorted.reverse() : sorted
}

const getStaticQueryProducts = (pageData, readStaticQuery) => {
  if (typeof readStaticQuery !== 'function') return []

  for (const hash of pageData?.staticQueryHashes || []) {
    const nodes = readStaticQuery(hash)?.data?.allContentstackProductV2?.nodes
    if (
      nodes?.some(
        (product) =>
          product?.taxonomies?.length && product?.fund_classes?.length
      )
    ) {
      return nodes
    }
  }
  return []
}

const dedupeEntitiesByUrl = (entities) => {
  const unique = new Map()
  ;(entities || []).forEach((entity) => {
    const key = structuredData.normalizePathname(entity?.url || '')
    if (entity?.title && key && key !== '/') unique.set(key, entity)
  })
  return Array.from(unique.values())
}

const getInvestmentTeamOrganizations = (page = {}) =>
  dedupeEntitiesByUrl(
    (page?.product_grouping || [])
      .flatMap((grouping) => grouping?.group || [])
      .flatMap((group) => group?.products || [])
      .flatMap((product) => product?.product_subadviser || [])
      .flatMap((subadviser) => subadviser?.subadviser_firm || [])
  )

const getLeadershipPeople = (page = {}) =>
  dedupeEntitiesByUrl([
    ...(page?.people || []),
    ...(page?.investment_product_leadership || []),
  ])

const buildStructuredDataFromPageData = (
  pageData,
  { readStaticQuery } = {}
) => {
  const kind = getPageKind(pageData)
  const result = pageData?.result || {}
  const data = result.data || {}
  const pageContext = result.pageContext || {}
  const pathname = pageData?.path || pageContext?.url
  if (!kind || !pathname || pageContext?.isAuthenticated) return null

  if (kind === 'product') {
    const product = data.contentstackProductV2
    const fundClass = product?.fund_classes?.find(
      (item) =>
        String(item?.ticker || '').toLowerCase() ===
        String(pageContext?.ticker || '').toLowerCase()
    )
    if (!product || !fundClass) return null

    const breadcrumbItems = structuredData.getProductBreadcrumbItems({
      product,
      fundClass,
      pathname,
    })
    return {
      kind,
      graph: structuredData.buildProductGraph({
        product,
        fundClass,
        pathname,
        breadcrumbItems,
      }),
    }
  }

  if (kind === 'article' || kind === 'news-article') {
    const article =
      kind === 'article'
        ? data.contentstackInsightArticle
        : data.contentstackNewsArticle
    if (!article) return null

    return {
      kind,
      graph: entityStructuredData.buildArticleGraph({
        article,
        pathname,
        articleType: kind === 'news-article' ? 'NewsArticle' : 'Article',
        reviewers:
          article?.reviewers || article?.reviewed_by || article?.reviewedBy,
      }),
    }
  }

  if (kind === 'author-profile') {
    const entity = data.contentstackPeople
    if (!entity) return null
    return {
      kind,
      graph: entityStructuredData.buildProfileGraph({
        entity,
        pathname,
        profileType: 'author',
      }),
    }
  }

  if (kind === 'investment-team') {
    const team = data.contentstackSubadviserFirm
    if (!team) return null
    return {
      kind,
      graph: entityStructuredData.buildInvestmentTeamGraph({
        team,
        pathname,
      }),
    }
  }

  if (kind === 'investment-team-member') {
    const team = data.contentstackSubadviserFirm
    const person = team?.people?.find(
      (member) => member?.id === pageContext?.id
    )
    if (!team || !person) return null
    return {
      kind,
      graph: entityStructuredData.buildProfileGraph({
        entity: person,
        pathname,
        profileType: 'investment-team-member',
        organization: team,
      }),
    }
  }

  if (kind === 'leadership-profile') {
    const person = data.contentstackPeople
    if (!person) return null
    return {
      kind,
      graph: entityStructuredData.buildProfileGraph({
        entity: person,
        pathname,
        profileType: 'leadership',
        organization: {
          title: 'Harbor Capital Advisors, Inc.',
          url: '/',
        },
      }),
    }
  }

  if (kind === 'investment-teams') {
    const page = data.contentstackInvestmentTeams
    if (!page) return null
    return {
      kind,
      graph: entityStructuredData.buildEntityCollectionGraph({
        page,
        pathname,
        entities: getInvestmentTeamOrganizations(page),
        entityType: 'organization',
      }),
    }
  }

  if (kind === 'leadership-collection') {
    const page = data.allContentstackLeadership?.nodes?.[0]
    if (!page) return null
    return {
      kind,
      graph: entityStructuredData.buildEntityCollectionGraph({
        page,
        pathname,
        entities: getLeadershipPeople(page),
        entityType: 'person',
      }),
    }
  }

  if (kind === 'organization-page') {
    const page = data.contentstackLandingPage
    if (!page) return null
    return {
      kind,
      graph: entityStructuredData.buildSiteOrganizationGraph({
        page,
        pathname,
      }),
    }
  }

  const page =
    kind === 'collection'
      ? data.contentstackAllFundPage
      : data.contentstackLandingPage
  let products = []

  if (kind === 'collection') {
    const productType = page?.share_classes?.[0]?.product_type
    const shareClassTitle = pageContext?.shareclass?.title
    const pageProductTypes = new Set(
      (page?.share_classes || []).map(
        (shareClass) =>
          SHARE_CLASS_TO_PRODUCT_TYPE[shareClass?.product_type] ||
          shareClass?.product_type
      )
    )
    products = (data.products?.nodes || [])
      .filter((product) => pageProductTypes.has(product?.product_type))
      .sort((a, b) =>
        String(a?.title || '').localeCompare(String(b?.title || ''))
      )
      .map((product) => ({
        ...product,
        path: productPaths.getProductPath(product, {
          preferredProductType: productType,
          preferredShareClassTitle: shareClassTitle,
        }),
      }))
  } else {
    const suiteHero = page?.hero?.find(
      (hero) => hero?.__typename === PRODUCT_SUITE_HERO
    )
    const suiteTerm = suiteHero?.suite_taxonomies?.find(
      (taxonomy) => taxonomy?.taxonomy_uid === 'fund_suites'
    )
    const suiteProducts = getStaticQueryProducts(pageData, readStaticQuery)
    products = sortSuiteProducts(
      suiteProducts.filter((product) =>
        product?.taxonomies?.some(
          (taxonomy) =>
            taxonomy?.taxonomy_uid === suiteTerm?.taxonomy_uid &&
            taxonomy?.term_uid === suiteTerm?.term_uid
        )
      ),
      Boolean(suiteHero?.suite_reverse_alpha_ordering)
    ).map((product) => ({
      ...product,
      path: productPaths.getProductPath(product) || product?.url,
    }))
  }

  const canonicalProducts = products.filter((product) => product?.path)
  if (!page || !canonicalProducts.length) return null
  const breadcrumbItems = structuredData.getCollectionBreadcrumbItems({
    title: page?.title,
    pathname,
  })

  return {
    kind,
    graph: structuredData.buildCollectionGraph({
      page,
      products: canonicalProducts,
      pathname,
      breadcrumbItems,
    }),
  }
}

module.exports = {
  buildStructuredDataFromPageData,
  getPageKind,
  sortSuiteProducts,
}
